import { push } from 'redux-first-history';
import { all, takeEvery, put, call, select } from 'redux-saga/effects';
import actions from './actions';
import { tableConfig } from '../../settings/index';
import Api from '../../helpers/api';
import { sendRequest } from '../../helpers/saga';
import { logger } from '../../helpers/logger';
import notifications from '../../redux/notifications/actions';
import { t } from '../../i18n';

const administratorsApi = new Api('administrators/list');
const api = new Api('notifications');
const subscriptionsListApi = new Api('notifications/subscriptions');
const subscriptionsApi = new Api('notifications/subscriptions/:id');
const unsubscriptionsApi = new Api('notifications/:id/assignments/:assignment_id');
const assignApi = new Api('notifications/:id/assignments');
const resolveApi = new Api('notifications/resolve');
const unresolveApi = new Api('notifications/unresolve');
const topNavNotificationsApi = new Api('notifications/top_nav');

function* loadTopNavNotifications() {
  try {
    const result = yield call(sendRequest, topNavNotificationsApi.load, {});

    if (result) {
      const { data } = result.data.success;
      yield put(actions.loadTopNavNotificationsSuccess({ notifications: data.records }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadData(data) {
  try {
    const {
      page = 1,
      pageSize = tableConfig.pageSize,
      column,
      order,
      tab = 'active',
      category_id = 'assigned-to-you',
      filter = '',
    } = data.payload.query;

    const result = yield call(
      sendRequest,
      category_id === 'assigned-to-you' ? api.load : subscriptionsApi.load,
      {
        search: {
          page,
          pageSize,
          column,
          order,
          filter,
          tab,
        },
        ...(category_id !== 'assigned-to-you' && { id: category_id }),
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      const { data: responseData } = result.data.success;
      const { records, totalCount, filteredCount, sortable_columns, subscriptions, available_categories } =
        responseData;

      yield put(
        actions.loadDataSuccess({
          records,
          subscriptions,
          available_categories,
          total: totalCount,
          filteredTotal: filteredCount,
          sortableColumns: sortable_columns,
          tab,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadAdministrators() {
  try {
    const result = yield call(
      sendRequest,
      administratorsApi.load,
      { search: { pageSize: 2000 } },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data && result.data.data && result.data.data.length > 0) {
      const { data } = result.data;
      const administrators = [];

      data.forEach(admin => {
        administrators.push({
          id: admin.id,
          name: `${admin.first_name} ${admin.last_name}`,
        });
      });

      yield put(actions.loadAdministratorsSuccess({ administrators }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* resolveNotification({ payload }) {
  const { data, query } = payload;

  try {
    const result = yield call(
      sendRequest,
      resolveApi.updateItem,
      {
        data: {
          notifications: data,
        },
      },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield loadData({ payload: { query } });
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.resolved_successfully', {
            item: t('dash.notificationsCenter.notifications_title'),
          }),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* unresolveNotification({ payload }) {
  const { data, query } = payload;

  try {
    const result = yield call(
      sendRequest,
      unresolveApi.updateItem,
      {
        data: {
          notifications: data,
        },
      },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield loadData({ payload: { query } });
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.unresolved_successfully', {
            item: t('dash.notificationsCenter.notifications_title'),
          }),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* unsubscribe({ payload }) {
  const { id, query } = payload;

  const result = yield call(
    sendRequest,
    subscriptionsApi.multipleParamsDeleteItem,
    { params: { id } },
    notifications.REQUEST_ERROR,
  );

  if (result && result.data) {
    if (query.category_id === id) {
      const notificationsCenterState = yield select(state => state.NotificationsCenter);
      let { activeNotifications: dataSource } = notificationsCenterState;

      if (query.tab === 'resolved') {
        dataSource = notificationsCenterState.resolvedNotifications;
      }

      const categoryTabIndex = dataSource.subscriptions.map(item => item.id).indexOf(id);
      const categoryTab =
        (dataSource.subscriptions[categoryTabIndex - 1] && dataSource.subscriptions[categoryTabIndex - 1].id) ||
        'assigned-to-you';

      yield put(push(`/notifications?category_id=${categoryTab}&tab=${query.tab}`));
    } else {
      yield loadData({ payload: { query } });
    }

    yield put(actions.unsubscribeSuccess());
    yield put(
      notifications.addNotification({
        headline: t('dash.common.notification.success_headline'),
        type: 'success',
        message: t('dash.notificationsCenter.unsubscribe_notification'),
      }),
    );
  }
}

function* subscribe({ payload }) {
  const { category_id, query } = payload;

  const result = yield call(
    sendRequest,
    subscriptionsListApi.multipleParamsCreateItem,
    { data: { subscription: { category: category_id } } },
    notifications.REQUEST_ERROR,
  );

  if (result && result.data) {
    yield loadData({ payload: { query } });
    yield put(actions.subscribeSuccess());
    yield put(
      notifications.addNotification({
        headline: t('dash.common.notification.success_headline'),
        type: 'success',
        message: t('dash.notificationsCenter.subscribe_notification'),
      }),
    );
  }
}

function* assign({ payload }) {
  const { admin_id, notification_id, query } = payload;

  const result = yield call(
    sendRequest,
    assignApi.multipleParamsCreateItem,
    { data: { assignment: { admin_id } }, params: { id: notification_id } },
    notifications.REQUEST_ERROR,
  );

  if (result && result.data) {
    yield loadData({ payload: { query } });
    yield put(actions.assignSuccess());
    yield put(
      notifications.addNotification({
        headline: t('dash.common.notification.success_headline'),
        type: 'success',
        message: t('dash.common.notification.assigned_successfully', {
          item: t('dash.notificationsCenter.notifications_title'),
        }),
      }),
    );
  }
}

function* unassign({ payload }) {
  const { assignment_id, notification_id, query } = payload;

  const result = yield call(
    sendRequest,
    unsubscriptionsApi.multipleParamsDeleteItem,
    { params: { id: notification_id, assignment_id } },
    notifications.REQUEST_ERROR,
  );

  if (result && result.data) {
    yield loadData({ payload: { query } });
    yield put(actions.assignSuccess());
    yield put(
      notifications.addNotification({
        headline: t('dash.common.notification.success_headline'),
        type: 'success',
        message: t('dash.common.notification.unassigned_successfully', {
          item: t('dash.notificationsCenter.notifications_title'),
        }),
      }),
    );
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.ASSIGN_REQUEST, assign),
    takeEvery(actions.LOAD_ADMINISTRATORS_REQUEST, loadAdministrators),
    takeEvery(actions.LOAD_DATA_REQUEST, loadData),
    takeEvery(actions.LOAD_TOP_NAV_NOTIFICATIONS_REQUEST, loadTopNavNotifications),
    takeEvery(actions.RESOLVE_NOTIFICATION, resolveNotification),
    takeEvery(actions.SUBSCRIBE, subscribe),
    takeEvery(actions.UNASSIGN_REQUEST, unassign),
    takeEvery(actions.UNRESOLVE_NOTIFICATION, unresolveNotification),
    takeEvery(actions.UNSUBSCRIBE, unsubscribe),
  ]);
}
